import { MapPin, X, Phone, User } from "lucide-react";
import AddressDropdown from "../inputs/AddressDropdown";
import CitySelectorForm from "../inputs/CitySelectorForm";
import { useEffect } from "react";

type MultipleDestination = {
  id?: string;
  destinatie: string;
  oras_destinatie: string;
  locatie_destinatie_id?: number;
  descriere?: string;
  telefon_destinatar?: string;
  nume_destinatar?: string;
};

interface MultipleDestinationsSectionProps {
  formData: any;
  setFormData: (data: any) => void;
}

const MultipleDestinationsSection: React.FC<MultipleDestinationsSectionProps> = ({
  formData,
  setFormData
}) => {
  // Auto-setează create_separate_transports când există destinații suplimentare
  useEffect(() => {
    const hasDestinations = formData.multiple_destinations && formData.multiple_destinations.length > 0;
    
    setFormData(prev => ({
      ...prev,
      create_separate_transports: hasDestinations
    }));
  }, [formData.multiple_destinations?.length, setFormData]);

  const addNewDestination = () => {
    const newDestination: MultipleDestination = {
      id: Date.now().toString(),
      destinatie: '',
      oras_destinatie: '',
      locatie_destinatie_id: undefined,
      descriere: '',
      telefon_destinatar: '',
      nume_destinatar: ''
    };
    setFormData(prev => ({
      ...prev,
      multiple_destinations: [...(prev.multiple_destinations || []), newDestination]
    }));
  };

  const removeDestination = (destinationId: string) => {
    setFormData(prev => ({
      ...prev,
      multiple_destinations: prev.multiple_destinations?.filter(d => d.id !== destinationId) || []
    }));
  };

  const updateDestination = (destinationId: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      multiple_destinations: prev.multiple_destinations?.map(d => 
        d.id === destinationId 
          ? { ...d, [field]: value }
          : d
      ) || []
    }));
  };

  const updateDestinationLocation = (destinationId: string, location: any) => {
    setFormData(prev => ({
      ...prev,
      multiple_destinations: prev.multiple_destinations?.map(d => 
        d.id === destinationId 
          ? { 
              ...d, 
              locatie_destinatie_id: location?.id,
              destinatie: location?.adresa || ''
            }
          : d
      ) || []
    }));
  };

  const updateDestinationCity = (destinationId: string, city: any) => {
    setFormData(prev => ({
      ...prev,
      multiple_destinations: prev.multiple_destinations?.map(d => 
        d.id === destinationId 
          ? { ...d, oras_destinatie: city?.name || '' }
          : d
      ) || []
    }));
  };

  return (
    <div className="bg-gradient-to-r from-cyan-50 to-teal-50 rounded-xl p-4 border border-cyan-200 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <div className="p-2 bg-cyan-500 rounded-lg mr-3">
            <MapPin className="w-4 h-4 text-white" />
          </div>
          <h3 className="font-semibold text-gray-800 text-lg">Destinații Suplimentare</h3>
        </div>
        <button
          type="button"
          onClick={addNewDestination}
          className="px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 text-sm flex items-center gap-2 transition-colors"
        >
          <MapPin className="w-4 h-4" />
          Adaugă Destinație
        </button>
      </div>

      {formData.multiple_destinations && formData.multiple_destinations.length > 0 && (
        <div className="mb-4">
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-700 font-medium">
              📦 Se vor crea {(formData.multiple_destinations?.length || 0) + 1} transporturi separate
            </p>
            <p className="text-xs text-blue-600 mt-1">
              Unul pentru destinația principală + {formData.multiple_destinations?.length || 0} pentru destinațiile suplimentare
            </p>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {formData.multiple_destinations?.map((destination, index) => (
          <div key={destination.id} className="p-4 bg-white rounded-xl border border-cyan-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-gray-800 flex items-center">
                <div className="w-7 h-7 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-full flex items-center justify-center text-white text-sm font-bold mr-3">
                  {index + 2}
                </div>
                Destinația {index + 2}
              </h4>
              <button
                type="button"
                onClick={() => removeDestination(destination.id)}
                className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            {/* Location Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Oraș destinație *
                </label>
                <CitySelectorForm
                  selectedCity={destination.oras_destinatie || ''}
                  onCityChange={(city) => updateDestinationCity(destination.id, city)}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adresa destinație *
                </label>
                <AddressDropdown
                  value={destination.locatie_destinatie_id ? {
                    id: destination.locatie_destinatie_id,
                    adresa: destination.destinatie || '',
                    oras: destination.oras_destinatie || '',
                    tara: 'Romania',
                    tip: 'destinatie'
                  } : null}
                  onChange={(location) => updateDestinationLocation(destination.id, location)}
                  city={destination.oras_destinatie || ''}
                  type="destinatie"
                  placeholder="Caută sau adaugă adresa"
                  required
                />
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-gradient-to-r from-cyan-25 to-teal-25 rounded-lg p-4 border border-cyan-100 mb-4">
              <div className="flex items-center mb-3">
                <div className="p-1.5 bg-cyan-100 rounded-lg mr-2">
                  <User className="w-4 h-4 text-cyan-600" />
                </div>
                <h5 className="font-medium text-gray-800">Contact Destinație {index + 2}</h5>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <User className="w-4 h-4 inline mr-1" />
                    Nume destinatar
                  </label>
                  <input
                    type="text"
                    value={destination.nume_destinatar || ''}
                    onChange={(e) => updateDestination(destination.id, 'nume_destinatar', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors"
                    placeholder="Numele firmei"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Phone className="w-4 h-4 inline mr-1" />
                    Telefon destinatar *
                  </label>
                  <input
                    type="tel"
                    value={destination.telefon_destinatar || ''}
                    onChange={(e) => updateDestination(destination.id, 'telefon_destinatar', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors"
                    placeholder="Ex: +40721654321"
                    required
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Contactul persoanei care va primi coletul la această destinație
              </p>
            </div>
            
            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descriere specifică pentru această destinație
              </label>
              <textarea
                value={destination.descriere || ''}
                onChange={(e) => updateDestination(destination.id, 'descriere', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 resize-none transition-colors"
                rows={2}
                placeholder="Detalii specifice pentru această destinație..."
              />
            </div>
          </div>
        )) || []}
        
        {(!formData.multiple_destinations || formData.multiple_destinations.length === 0) && (
          <div className="text-center py-12 text-gray-500">
            <div className="max-w-sm mx-auto">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-8 h-8 text-gray-400" />
              </div>
              <h4 className="font-medium text-gray-700 mb-2">Nu au fost adăugate destinații suplimentare</h4>
              <p className="text-sm text-gray-500 mb-4">
                Poți adăuga multiple destinații pentru același transport. Fiecare va avea contactul său.
              </p>
              <button
                type="button"
                onClick={addNewDestination}
                className="px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 text-sm flex items-center gap-2 mx-auto transition-colors"
              >
                <MapPin className="w-4 h-4" />
                Adaugă Prima Destinație
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MultipleDestinationsSection;